import React from 'react';
import { Mail, Phone, StickyNote, X } from 'lucide-react';
import type { ContactCardProps } from './types';

/**
 * Read-only display of a single survey contact. Used both inside
 * {@link ContactEditor} (with `onRemove`) and as a standalone display card for
 * contacts already on file (no `onRemove`). Surfaces rep-typed `notes` so they
 * are visible end-to-end (audit A8).
 */
export function ContactCard({
  name,
  role,
  company,
  phone,
  email,
  notes,
  sourceLabel,
  onRemove,
}: ContactCardProps): React.ReactElement {
  const subtitle = [role, company].filter(Boolean).join(' · ');
  return (
    <div className="flex items-start justify-between rounded-xl border border-slate-200 bg-white p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-slate-900">{name || 'Unnamed contact'}</p>
          {sourceLabel && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
              {sourceLabel}
            </span>
          )}
        </div>
        {subtitle && <p className="mt-0.5 text-[11px] font-medium text-slate-500">{subtitle}</p>}
        {(phone || email) && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            {phone && (
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Phone size={10} /> {phone}
              </span>
            )}
            {email && (
              <span className="flex min-w-0 items-center gap-1 text-xs text-slate-500">
                <Mail size={10} className="flex-none" /> <span className="truncate">{email}</span>
              </span>
            )}
          </div>
        )}
        {notes && (
          <p className="mt-1 flex items-start gap-1 whitespace-pre-wrap text-xs text-slate-500">
            <StickyNote size={10} className="mt-0.5 flex-none" /> <span>{notes}</span>
          </p>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove contact"
          className="flex min-h-[44px] min-w-[44px] flex-none items-center justify-center text-slate-400 hover:text-red-500 active:text-red-500"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
